// Claude: shared admin page for Buyer FAQ, Seller FAQ, AI Knowledge Base — full CRUD 13-03-2026
// Claude: added ai_common type + Sync to Production modal 25-03-2026
import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Text, Textarea, Input, Switch, FormControl, FormLabel, Badge, Spinner } from "@chakra-ui/react";
import { Modal, Table } from "antd";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

type Entry = {
  _id: string;
  question: string;
  answer: string;
  isActive: boolean;
};

type Props = {
  type: "buyer_faq" | "seller_faq" | "ai_buyer" | "ai_seller" | "ai_common";
  title: string;
  hint?: string; // Claude: fine print shown at top of page 15-03-2026
};

const KnowledgeBasePage = ({ type, title, hint }: Props) => {
  const { success, error } = useToastWrapper();
  const [data, setData] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Claude: Sync to Production modal state 25-03-2026
  // Flow: admin clicks button → modal opens → pastes prod URI → syncs → shows result
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncUri, setSyncUri] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<number | null>(null); // null = not yet synced

  const handleSync = async () => {
    if (!syncUri.trim()) { error("Please enter the Production MongoDB URI"); return; }
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await AdminAPI.syncKnowledgeToProduction(syncUri.trim(), type);
      // Claude: ResponseInterceptor wraps all responses — value is at res.data.synced 26-03-2026
      setSyncResult(res?.data?.synced ?? res?.synced ?? 0);
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncModalClose = () => {
    setSyncModalOpen(false);
    setSyncUri("");
    setSyncResult(null);
  };

  // Add / Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Entry | null>(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isActive, setIsActive] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getKnowledge(type);
      setData(res?.data ?? res ?? []);
    } catch (err) {
      error(errorHandler(err));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [type]);

  const openAdd = () => {
    setEditTarget(null);
    setQuestion("");
    setAnswer("");
    setIsActive(true);
    setModalOpen(true);
  };

  const openEdit = (entry: Entry) => {
    setEditTarget(entry);
    setQuestion(entry.question);
    setAnswer(entry.answer);
    setIsActive(entry.isActive);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!question.trim() || !answer.trim()) { error("Question and answer are required"); return; }
    setSaving(true);
    try {
      if (editTarget) {
        await AdminAPI.updateKnowledge(editTarget._id, { question: question.trim(), answer: answer.trim(), isActive });
        success("Entry updated");
      } else {
        await AdminAPI.createKnowledge({ question: question.trim(), answer: answer.trim(), type });
        success("Entry added");
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      error(errorHandler(err));
    }
    setSaving(false);
  };

  const handleDelete = (entry: Entry) => {
    Modal.confirm({
      title: "Delete entry?",
      content: `"${entry.question}" will be permanently deleted.`,
      okText: "Yes, Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await AdminAPI.deleteKnowledge(entry._id);
          success("Entry deleted");
          await load();
        } catch (err) {
          error(errorHandler(err));
        }
      },
    });
  };

  const columns = [
    {
      title: "Question",
      dataIndex: "question",
      render: (text: string) => <Text fontSize="sm" fontWeight={500}>{text}</Text>,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      render: (text: string) => <Text fontSize="sm" color="gray.600" noOfLines={2}>{text}</Text>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (val: boolean) => (
        <Badge colorScheme={val ? "green" : "red"} textTransform="none">
          {val ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      width: 140,
      render: (_: any, record: Entry) => (
        <Flex gap={2}>
          <Button size="xs" bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} onClick={() => openEdit(record)}>Edit</Button>
          <Button size="xs" bg="#C62828" color="white" _hover={{ bg: "#B71C1C" }} onClick={() => handleDelete(record)}>Delete</Button>
        </Flex>
      ),
    },
  ];

  return (
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={hint ? 1 : 4}>
        {/* Claude: show entry count next to title so admins can spot local vs production mismatches 26-03-2026 */}
        <Flex alignItems="baseline" gap={2}>
          <Text fontSize="xl" fontWeight={700}>{title}</Text>
          {!loading && <Text fontSize="sm" color="gray.500">({data.length} {data.length === 1 ? "entry" : "entries"})</Text>}
        </Flex>
        <Flex gap={2}>
          {/* Claude: Sync to Production — only shown on AI KB pages (not FAQ pages) 25-03-2026 */}
          {["ai_buyer", "ai_seller", "ai_common"].includes(type) && (
            <Button size="sm" bg="#7C66FF" color="white" _hover={{ bg: "#5C4FCC" }} onClick={() => setSyncModalOpen(true)}>
              Sync to Production
            </Button>
          )}
          <Button size="sm" bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} onClick={openAdd}>+ Add Entry</Button>
        </Flex>
      </Flex>
      {/* Claude: fine print hint shown on AI KB pages 15-03-2026 */}
      {hint && <Text fontSize="xs" color="gray.500" mb={4}>{hint}</Text>}

      <Table
        dataSource={data}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
      />

      {/* Claude: Sync to Production Modal — admin pastes prod URI, syncs, sees result 25-03-2026
          Full replace: all entries of this type in production are deleted and replaced with local entries. */}
      <Modal
        open={syncModalOpen}
        title="Sync to Production"
        onCancel={handleSyncModalClose}
        footer={null}
        width={560}
      >
        <Box pt={2} pb={2}>
          {syncResult === null ? (
            <>
              <Text fontSize="xs" color="gray.500" mb={1}>Production MongoDB URI</Text>
              <Input
                value={syncUri}
                onChange={(e) => setSyncUri(e.target.value)}
                placeholder="mongodb+srv://..."
                mb={3}
                focusBorderColor="#7C66FF"
              />
              <Text fontSize="xs" color="orange.500" mb={4}>
                Local <strong>{type}</strong> entries will be upserted into production (insert new, update existing by question). Production-only entries are not affected.
              </Text>
              <Flex gap={2} justifyContent="flex-end">
                <Button size="sm" variant="outline" onClick={handleSyncModalClose} isDisabled={syncing}>Cancel</Button>
                <Button size="sm" bg="#7C66FF" color="white" _hover={{ bg: "#5C4FCC" }}
                  isDisabled={syncing || !syncUri.trim()} onClick={handleSync}>
                  {syncing ? <Flex align="center" gap={2}><Spinner size="xs" /><Text fontSize="sm">Syncing...</Text></Flex> : "Confirm Sync"}
                </Button>
              </Flex>
            </>
          ) : (
            // Claude: success state — show count of synced entries 25-03-2026
            <Flex direction="column" alignItems="center" py={4} gap={3}>
              <Text fontSize="2xl">✅</Text>
              <Text fontSize="md" fontWeight={600} color="green.600">Sync Complete</Text>
              <Text fontSize="sm" color="gray.600">
                <strong>{syncResult}</strong> {syncResult === 1 ? "entry" : "entries"} synced to production successfully.
              </Text>
              <Button size="sm" bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} onClick={handleSyncModalClose}>
                Close
              </Button>
            </Flex>
          )}
        </Box>
      </Modal>

      {/* Add / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editTarget ? "Edit Entry" : "Add Entry"}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={600}
      >
        <Box pt={2} pb={2}>
          <Text fontSize="xs" color="gray.500" mb={1}>Question</Text>
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter question"
            mb={3}
            focusBorderColor="#7C66FF"
          />
          <Text fontSize="xs" color="gray.500" mb={1}>Answer</Text>
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Enter answer"
            rows={5}
            mb={3}
            focusBorderColor="#7C66FF"
          />
          <Flex gap={4} alignItems="center" mb={4}>
            {editTarget && (
              <FormControl display="flex" alignItems="center" w="auto">
                <FormLabel fontSize="sm" mb={0} mr={2}>Active</FormLabel>
                <Switch colorScheme="green" isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              </FormControl>
            )}
          </Flex>
          <Flex gap={2} justifyContent="flex-end">
            <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button size="sm" bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} isLoading={saving} onClick={handleSave}>
              {editTarget ? "Update" : "Add"}
            </Button>
          </Flex>
        </Box>
      </Modal>
    </Box>
  );
};

export default KnowledgeBasePage;
